import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';

const BASE = import.meta.env.BASE_URL;

const images = {
  hero: `${BASE}home-template/hero-approved.webp`,
  notes: `${BASE}home-template/notes.jpg`,
  rhythm: `${BASE}home-template/rhythm.jpg`,
  symbols: `${BASE}home-template/symbols.jpg`,
  reading: `${BASE}home-template/reading.jpg`,
  practice: `${BASE}home-template/practice.jpg`,
  footer: `${BASE}home-template/footer.jpg`,
};

const heroCards = [
  {
    icon: 'ri-book-open-line',
    title: 'Comece entendendo',
    text: 'Primeiro a pauta e a clave. Só depois o sistema começa a cobrar respostas.',
  },
  {
    icon: 'ri-repeat-2-line',
    title: 'Repita até fixar',
    text: 'O mesmo tema aparece em muitas variações para virar memória visual e motora.',
  },
  {
    icon: 'ri-trophy-line',
    title: 'Avance por domínio',
    text: 'A dificuldade cresce aos poucos e a próxima etapa só abre quando há segurança.',
  },
];

const sampleCards = [
  {
    image: images.notes,
    eyebrow: 'Primeiro contato',
    title: 'Entenda a pauta',
    text: 'Veja as cinco linhas, os quatro espaços e a referência da clave de sol antes de responder qualquer exercício.',
    icon: 'ri-music-2-line',
  },
  {
    image: images.rhythm,
    eyebrow: 'Memória visual',
    title: 'Decore as posições',
    text: 'Acompanhe as notas subindo e descendo até começar a reconhecer cada posição sem depender do nome escrito.',
    icon: 'ri-eye-line',
  },
  {
    image: images.symbols,
    eyebrow: 'Treino guiado',
    title: 'Responda no teclado',
    text: 'A nota permanece na tela até a tecla correta ser tocada. Errar não faz o exercício avançar sozinho.',
    icon: 'ri-keyboard-line',
  },
  {
    image: images.reading,
    eyebrow: 'Domínio',
    title: 'Misture e acelere',
    text: 'Depois entram linhas e espaços misturados, sorteio aleatório e tempo de resposta cada vez menor.',
    icon: 'ri-speed-up-line',
  },
  {
    image: images.practice,
    eyebrow: 'Revisão automática',
    title: 'Volte quando precisar',
    text: 'Se o desempenho cair muito, o sistema retorna uma etapa para reforçar a base antes de seguir.',
    icon: 'ri-arrow-go-back-line',
  },
  {
    image: images.footer,
    eyebrow: 'Objetivo',
    title: 'Leia um trecho simples',
    text: 'No fim da trilha inicial, a meta é juntar leitura, ritmo e símbolos para tocar uma partitura simples.',
    icon: 'ri-flag-line',
  },
];

const progression = [
  {
    number: '01',
    title: 'Região central',
    text: 'Dó4 a Si4, começando com sequência, depois linhas, espaços e mistura.',
    icon: 'ri-focus-3-line',
  },
  {
    number: '02',
    title: 'Região aguda',
    text: 'O mesmo método acima do Si, sem trocar a lógica que o aluno já aprendeu.',
    icon: 'ri-arrow-up-line',
  },
  {
    number: '03',
    title: 'Região grave',
    text: 'A leitura desce abaixo do Dó central e incorpora as linhas suplementares.',
    icon: 'ri-arrow-down-line',
  },
  {
    number: '04',
    title: 'Leitura completa',
    text: 'As três regiões são misturadas até o reconhecimento ficar praticamente automático.',
    icon: 'ri-shuffle-line',
  },
];


// Conteúdo editorial simples da home pública.
// É apresentação e apoio ao iniciante; não substitui as aulas internas.
const HISTORY = [
  ['Antes da pauta', 'Músicos já usavam sinais e letras para registrar ideias musicais, mas os sistemas variavam muito.'],
  ['Idade Média', 'Surgiram os neumas, sinais colocados sobre textos para indicar o movimento aproximado da melodia.'],
  ['Século XI', 'Guido de Arezzo ajudou a organizar o ensino musical com linhas de referência e solmização.'],
  ['Pauta moderna', 'Com o tempo, a escrita evoluiu até a pauta de cinco linhas e os símbolos que usamos hoje.'],
];

const supportPoints = [
  'Não substitui o professor',
  'Conta simples para guardar histórico',
  'Progresso salvo no servidor',
  'Exercícios com variações contínuas',
  'Dificuldade crescente, como em um jogo',
  'Revisão automática quando o domínio cai',
];

const beginnerSteps = [
  {
    icon: 'ri-seedling-line',
    title: 'Comece do zero',
    text: 'Você não precisa saber ler partitura. A proposta é criar familiaridade com a pauta, uma pequena ideia de cada vez.',
  },
  {
    icon: 'ri-time-line',
    title: 'Poucos minutos por dia',
    text: 'Sessões curtas e frequentes ajudam mais na fixação do que tentar decorar tudo de uma vez.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Reconheça antes de acelerar',
    text: 'Primeiro acerte a posição da nota. A velocidade só entra quando a leitura já estiver segura.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Use como apoio',
    text: 'O site simplifica a prática de notas, tempos e símbolos. Para técnica, interpretação e postura, o professor continua essencial.',
  },
];

const practiceTips = [
  ['ri-timer-2-line', 'Treine 5 a 10 minutos', 'É melhor praticar um pouco todos os dias do que fazer uma sessão enorme e ficar vários dias sem voltar.'],
  ['ri-volume-up-line', 'Fale o nome da nota', 'Antes de tocar, diga mentalmente “Dó”, “Ré”, “Mi”… Isso liga posição, nome e som.'],
  ['ri-arrow-up-down-line', 'Suba e desça a sequência', 'Treine Dó→Si e depois Si→Dó. O caminho inverso evita decorar apenas uma ordem automática.'],
  ['ri-layout-row-line', 'Separe linhas e espaços', 'Primeiro reconheça notas sobre as linhas, depois nos espaços. Só então misture as duas coisas.'],
  ['ri-focus-3-line', 'Priorize precisão', 'Não tente ser rápido cedo demais. Quando os acertos ficarem estáveis, reduza o tempo de resposta.'],
  ['ri-arrow-go-back-line', 'Volte quando errar demais', 'Se os erros aumentarem, revise uma etapa anterior. No sistema, essa revisão também pode acontecer automaticamente.'],
];

const curiosities = [
  {
    image: images.notes,
    label: 'Antes da pauta moderna',
    title: 'A música já era anotada de outras formas',
    text: 'Na Idade Média, sinais chamados neumas ajudavam a lembrar o contorno das melodias. Com o tempo, linhas passaram a indicar alturas com mais precisão.',
  },
  {
    image: images.symbols,
    label: 'Século XI',
    title: 'Guido d’Arezzo ajudou a organizar o ensino',
    text: 'O monge e teórico Guido d’Arezzo ficou conhecido por sistematizar métodos de leitura e ensino com pauta de quatro linhas e sílabas como Ut, Ré, Mi, Fá, Sol e Lá.',
  },
  {
    image: images.reading,
    label: 'A pauta de hoje',
    title: 'Cinco linhas viraram o padrão',
    text: 'A escrita musical continuou evoluindo até a pauta de cinco linhas usada atualmente. Na clave de sol, a segunda linha de baixo para cima representa a nota Sol.',
  },
];

export default function Home() {
  const { user } = useAuth();

  const lessonHref = user ? '/aulas/1' : '/entrar?next=%2Faulas%2F1';
  const areaHref = user ? '/aulas' : '/entrar?next=%2Faulas';

  return (
    <div className="min-h-screen bg-white text-slate-950">
      <PublicHeader />

      <main>
        <section id="inicio" className="relative overflow-hidden bg-[#faf7f0] pt-16 lg:flex lg:min-h-[760px] lg:items-center lg:pt-[76px]">
          <img
            src={images.hero}
            alt="Menina praticando piano"
            width={1672}
            height={941}
            fetchPriority="high"
            className="relative block aspect-[4/3] w-full object-cover object-right-top sm:aspect-[1672/941] lg:absolute lg:inset-x-0 lg:bottom-0 lg:top-[76px] lg:h-[calc(100%-76px)] lg:object-cover lg:object-right-top"
          />
          <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-[66%] bg-gradient-to-r from-[#faf7f0]/95 via-[#faf7f0]/70 to-transparent lg:block" />

          <div className="relative z-10 mx-auto w-full max-w-[1480px] px-5 py-9 sm:px-8 sm:py-12 lg:px-10 lg:py-28">
            <div className="mx-auto max-w-4xl text-center lg:mx-0 lg:max-w-[620px] lg:text-left">
              <span className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/80 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.16em] text-orange-700 backdrop-blur-md">
                <i className="ri-music-2-fill text-base" />
                Leitura musical para iniciantes
              </span>

              <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-slate-950 sm:text-5xl lg:text-6xl xl:text-7xl">
                Aprenda a ler partituras
                <span className="block text-orange-600">praticando até dominar.</span>
              </h1>

              <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg lg:mx-0 lg:max-w-[540px] lg:text-xl">
                Uma ferramenta de apoio para memorizar notas, ritmo e símbolos com repetição,
                variações e dificuldade crescente — sem tentar substituir o professor.
              </p>

              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
                <Link
                  to={lessonHref}
                  className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-full bg-orange-500 px-7 py-4 text-sm font-extrabold text-white shadow-xl shadow-black/20 transition hover:bg-orange-600"
                >
                  <i className="ri-play-circle-fill text-xl" />
                  Fazer a primeira aula
                </Link>
                <Link
                  to={areaHref}
                  className="inline-flex min-w-[200px] items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-extrabold text-slate-900 shadow-xl shadow-black/15 transition hover:bg-orange-50"
                >
                  <i className="ri-user-3-line text-lg" />
                  {user ? 'Abrir área do aluno' : 'Entrar'}
                </Link>
              </div>

              <p className="mt-4 text-xs font-semibold text-slate-600 lg:max-w-[540px]">
                Entre na sua conta para guardar seus acertos e continuar de onde parou.
              </p>
            </div>

          </div>

          <a
            href="#amostras"
            aria-label="Ver conteúdo"
            className="absolute bottom-5 left-1/2 z-10 -translate-x-1/2 text-orange-600 hidden lg:block"
          >
            <i className="ri-arrow-down-line animate-bounce text-3xl" />
          </a>
        </section>

        <section aria-label="Como começar" className="bg-[#fffaf5] px-5 py-8 sm:px-8">
            <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-3">
              {heroCards.map((card) => (
                <article
                  key={card.title}
                  className="rounded-2xl border border-orange-100 bg-white p-5 text-center text-slate-950 shadow-sm"
                >
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-500 text-white">
                    <i className={`${card.icon} text-xl`} />
                  </span>
                  <h2 className="mt-4 text-base font-extrabold">{card.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{card.text}</p>
                </article>
              ))}
            </div>
        </section>

        <section id="preinicio" className="bg-white px-5 py-16 sm:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="grid items-center gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14">
              <div>
                <span className="inline-flex rounded-full bg-orange-100 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-orange-700">
                  Pré-início
                </span>
                <h2 className="mt-4 font-heading text-3xl font-extrabold text-slate-950 sm:text-4xl lg:text-5xl">
                  Nunca leu uma partitura? É justamente para começar.
                </h2>
                <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                  Esta página não pretende ensinar música inteira. Ela serve para você perder o medo
                  da pauta, entender os primeiros sinais e criar uma rotina de fixação antes ou entre
                  suas aulas com um professor.
                </p>
                <div className="mt-6 rounded-2xl border border-orange-100 bg-[#fff8ef] p-5">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-white">
                      <i className="ri-lightbulb-flash-line text-xl" />
                    </span>
                    <div>
                      <h3 className="font-extrabold text-slate-950">Pense como um aquecimento</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">
                        Você aprende uma referência, repete em variações e acompanha sua evolução.
                        Quando estiver seguro, a dificuldade sobe.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {beginnerSteps.map((item) => (
                  <article key={item.title} className="rounded-2xl border border-slate-200 bg-[#fbfbfc] p-5 shadow-sm">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <h3 className="mt-4 text-base font-extrabold text-slate-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="amostras" className="bg-[#f7f8fa] px-5 py-16 sm:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex rounded-full bg-orange-100 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-orange-700">
                Veja antes de entrar
              </span>
              <h2 className="mt-4 font-heading text-3xl font-extrabold text-slate-950 sm:text-4xl lg:text-5xl">
                O que você vai praticar
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                Aqui na página pública você conhece o caminho. Os exercícios de verdade ficam dentro da área do aluno.
              </p>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {sampleCards.map((card) => (
                <article
                  key={card.title}
                  className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={card.image}
                      alt=""
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    <span className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/92 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-orange-700 backdrop-blur">
                      <i className={card.icon} />
                      {card.eyebrow}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-extrabold text-slate-950">{card.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{card.text}</p>
                    <Link
                      to={lessonHref}
                      className="mt-5 inline-flex items-center gap-2 text-sm font-extrabold text-orange-600 transition hover:gap-3"
                    >
                      Entrar para praticar
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="dicas" className="bg-white px-5 py-16 sm:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:items-start">
              <div className="lg:sticky lg:top-28">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-orange-600">
                  Dicas para treinar
                </span>
                <h2 className="mt-3 font-heading text-3xl font-extrabold text-slate-950 sm:text-4xl">
                  Pequenos hábitos ajudam a fixar muito mais.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">
                  Não é uma corrida. A meta é olhar a pauta e reconhecer a nota com cada vez menos esforço.
                  Use estas dicas também fora do sistema.
                </p>
                <Link
                  to={lessonHref}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 text-sm font-extrabold text-white hover:bg-orange-600"
                >
                  Experimentar na primeira aula
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {practiceTips.map(([icon, title, text], index) => (
                  <article key={title} className="rounded-2xl border border-slate-200 bg-[#fbfbfc] p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
                        <i className={`${icon} text-xl`} />
                      </span>
                      <span className="font-heading text-2xl font-black text-orange-200">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="mt-4 text-base font-extrabold text-slate-950">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="curiosidades" className="bg-[#f7f8fa] px-5 py-16 sm:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex rounded-full bg-orange-100 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-orange-700">
                Curiosidades
              </span>
              <h2 className="mt-4 font-heading text-3xl font-extrabold text-slate-950 sm:text-4xl lg:text-5xl">
                Como a música passou a ser escrita?
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                A notação não surgiu pronta. Ela foi sendo aperfeiçoada para ajudar músicos a lembrar,
                ensinar e executar melodias com mais precisão.
              </p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {curiosities.map((item) => (
                <article key={item.title} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <img src={item.image} alt="" className="h-52 w-full object-cover" />
                  <div className="p-6">
                    <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-orange-600">
                      {item.label}
                    </span>
                    <h3 className="mt-2 font-heading text-xl font-extrabold text-slate-950">{item.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.text}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-8 rounded-2xl border border-orange-100 bg-[#fff8ef] p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <i className="ri-information-line mt-0.5 text-xl text-orange-600" />
                <p className="text-sm leading-relaxed text-slate-700">
                  <strong className="text-slate-950">Curiosidade:</strong> a clave de sol também é chamada
                  de clave de G porque o seu desenho marca a linha do Sol. No pentagrama moderno,
                  essa referência fica na segunda linha de baixo para cima.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="historia" className="bg-[#171b22] px-5 py-16 text-white sm:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-[0.72fr_1.28fr]">
              <div>
                <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-orange-300">
                  História em poucos minutos
                </span>
                <h2 className="mt-3 font-heading text-3xl font-extrabold sm:text-4xl lg:text-5xl">
                  A partitura não nasceu pronta.
                </h2>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300">
                  A escrita musical foi sendo organizada durante séculos para ajudar músicos a
                  lembrar melodias, ensinar outras pessoas e tocar juntos com mais precisão.
                  Aqui fica só o essencial para despertar curiosidade.
                </p>
                <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
                  <p className="text-sm leading-relaxed text-slate-300">
                    <strong className="text-white">Guido d’Arezzo</strong>, no século XI, é uma das
                    figuras mais importantes dessa história. Ele ajudou a desenvolver formas mais
                    claras de representar alturas musicais e de ensinar canto. A notação continuou
                    evoluindo muito depois dele até chegar ao sistema atual.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="absolute bottom-0 left-[23px] top-0 w-px bg-white/15 sm:left-[31px]" />
                <div className="space-y-5">
                  {HISTORY.map(([period, text], index) => (
                    <article key={period} className="relative flex gap-4 sm:gap-6">
                      <span className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-4 border-[#171b22] bg-orange-500 text-sm font-black text-white sm:h-16 sm:w-16">
                        {index + 1}
                      </span>
                      <div className="flex-1 rounded-2xl border border-white/10 bg-white/[0.06] p-5 sm:p-6">
                        <h3 className="text-base font-extrabold text-orange-300">{period}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-slate-300">{text}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="metodo" className="bg-white px-5 py-16 sm:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-orange-600">
                Um método simples
              </span>
              <h2 className="mt-3 font-heading text-3xl font-extrabold text-slate-950 sm:text-4xl lg:text-5xl">
                Explicação primeiro. Cobrança depois.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                A pessoa entende o tema, observa exemplos, pratica guiada e só depois entra nos exercícios de domínio.
              </p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-2">
              <div className="relative min-h-[420px] overflow-hidden rounded-[28px]">
                <img src={images.practice} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/88 via-slate-950/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7 text-white sm:p-9">
                  <span className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">
                    Repetição inteligente
                  </span>
                  <h3 className="mt-2 font-heading text-3xl font-extrabold">
                    O sistema acompanha o aluno.
                  </h3>
                  <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/78">
                    Acertou, o domínio sobe. Errou, cai. Se começar a errar demais, o sistema pode
                    voltar uma etapa para reforçar a base antes de continuar.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  ['ri-eye-line', 'Observe', 'Veja a posição e escute antes de ser testado.'],
                  ['ri-keyboard-line', 'Pratique', 'Toque a resposta correta no teclado.'],
                  ['ri-shuffle-line', 'Misture', 'Linhas e espaços deixam de aparecer em ordem previsível.'],
                  ['ri-timer-flash-line', 'Acelere', 'Depois do domínio, o tempo de resposta começa a diminuir.'],
                  ['ri-arrow-go-back-line', 'Revise', 'Se o desempenho cair, uma etapa anterior volta a ser treinada.'],
                  ['ri-trophy-line', 'Domine', 'A próxima fase só abre quando o conhecimento está realmente firme.'],
                ].map(([icon, title, text]) => (
                  <article
                    key={title}
                    className="rounded-2xl border border-slate-200 bg-[#fbfbfc] p-5 shadow-sm"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
                      <i className={`${icon} text-xl`} />
                    </span>
                    <h3 className="mt-4 text-base font-extrabold text-slate-950">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="evolucao" className="bg-[#f7f8fa] px-5 py-16 sm:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex rounded-full bg-orange-100 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-orange-700">
                Evolução
              </span>
              <h2 className="mt-4 font-heading text-3xl font-extrabold text-slate-950 sm:text-4xl lg:text-5xl">
                A leitura cresce por regiões
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                O iniciante não recebe toda a pauta de uma vez. Cada região é dominada antes de misturar tudo.
              </p>
            </div>

            <div className="mt-12 space-y-5">
              {progression.map((item, index) => (
                <article
                  key={item.number}
                  className="grid overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm md:grid-cols-[190px_1fr_auto]"
                >
                  <div className="flex items-center gap-4 bg-slate-950 px-6 py-6 text-white md:flex-col md:justify-center md:py-8">
                    <span className="text-4xl font-black text-orange-400">{item.number}</span>
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-orange-300">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <div className="px-6 py-6 md:px-8 md:py-8">
                    <h3 className="font-heading text-2xl font-extrabold text-slate-950">{item.title}</h3>
                    <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">{item.text}</p>
                  </div>
                  <div className="flex items-center px-6 pb-6 md:px-8 md:pb-0">
                    <Link
                      to={lessonHref}
                      className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-3 text-sm font-extrabold text-white transition hover:bg-orange-600"
                    >
                      {index === 0 ? 'Começar' : 'Ver na área do aluno'}
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="apoio" className="bg-white px-5 py-16 sm:px-8 lg:py-20">
          <div className="mx-auto grid max-w-7xl overflow-hidden rounded-[30px] border border-orange-100 bg-[#fff8ef] shadow-lg shadow-orange-950/5 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="relative min-h-[340px]">
              <img src={images.reading} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#fff8ef]/20 lg:bg-gradient-to-l" />
            </div>
            <div className="flex items-center px-7 py-9 sm:px-10 lg:px-12">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1.5 text-xs font-extrabold text-orange-700">
                  <i className="ri-user-heart-line" />
                  Apoio ao aprendizado
                </span>
                <h2 className="mt-5 font-heading text-3xl font-extrabold text-slate-950 sm:text-4xl">
                  O professor ensina. O site ajuda a repetir.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">
                  A proposta é simples: entre uma aula e outra, o aluno pratica as mesmas habilidades
                  até reconhecer notas e símbolos com mais naturalidade.
                </p>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {supportPoints.map((point) => (
                    <div key={point} className="flex items-start gap-2 text-sm font-semibold text-slate-700">
                      <i className="ri-check-line mt-0.5 text-lg text-orange-600" />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to={lessonHref}
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-orange-500 px-7 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-orange-500/20 transition hover:bg-orange-600"
                >
                  <i className="ri-play-circle-fill text-xl" />
                  Fazer a primeira aula
                </Link>
              </div>
            </div>
          </div>
        </section>


        <section id="ajuda" className="bg-[#f7f8fa] px-5 py-16 sm:px-8">
          <div className="mx-auto max-w-3xl">
            <span className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-600">Dúvidas de quem está começando</span>
            <h2 className="mt-3 font-heading text-3xl font-extrabold text-slate-950 sm:text-4xl">Um passo de cada vez.</h2>
            <div className="mt-8 space-y-3">
              {[
                ['Preciso saber música para começar?', 'Não. Comece pela primeira aula: ela apresenta a pauta e a clave de sol antes dos exercícios. Observe as demonstrações e avance no seu ritmo.'],
                ['Preciso ter um piano?', 'Você pode começar com o teclado que aparece na tela, usando o mouse ou tocando nas teclas pelo celular. O objetivo inicial é reconhecer a posição das notas.'],
                ['Como meus pontos aumentam?', 'Cada resposta correta nos exercícios soma 5 XP. Ao dominar uma aula, você recebe 120 XP de bônus. Acompanhe seu nível pessoal na área do aluno; apenas tocar teclas fora do exercício não soma pontos.'],
                ['Quando posso passar para a próxima etapa?', 'A etapa mostra sua meta e a quantidade mínima de exercícios. Acerte com calma até atingir o domínio pedido. Se errar muito, revise antes de acelerar.'],
                ['O site substitui aulas com professor?', 'Ele é um apoio de iniciação e fixação de notas, tempos e símbolos. O professor orienta técnica, postura, interpretação e seu aprendizado musical de forma completa.'],
              ].map(([question, answer]) => (
                <details key={question} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <summary className="cursor-pointer font-bold text-slate-900">{question}</summary>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="relative min-h-[330px] overflow-hidden text-white">
          <img
            src={images.footer}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/94 via-slate-950/70 to-orange-950/35" />
          <div className="relative mx-auto flex min-h-[330px] max-w-7xl items-center px-6 py-12 sm:px-8">
            <div className="max-w-2xl">
              <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-orange-300">
                Da primeira nota ao primeiro trecho
              </span>
              <h2 className="mt-3 font-heading text-3xl font-extrabold sm:text-4xl lg:text-5xl">
                Entre na sala e comece a praticar.
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-white/78">
                Crie sua conta e pratique no seu ritmo. Na área do aluno, acompanhe suas aulas,
                seu histórico e os pontos conquistados em cada acerto.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to={lessonHref}
                  className="rounded-full bg-orange-500 px-7 py-3.5 text-sm font-extrabold text-white hover:bg-orange-600"
                >
                  Primeira aula
                </Link>
                <Link
                  to={areaHref}
                  className="rounded-full bg-white px-7 py-3.5 text-sm font-extrabold text-slate-950 hover:bg-orange-50"
                >
                  {user ? 'Abrir área do aluno' : 'Entrar'}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
